import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

interface PaginatedSelectProps<T> {
  value: string
  onChange: (value: string) => void
  placeholder: string
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onFetchNextPage: () => void
  options: T[]
  getOptionValue: (option: T) => string
  getOptionLabel: (option: T) => string
  disabled?: boolean
  className?: string
  loadingText?: string
  dropdownDirection?: 'auto' | 'up' | 'down'
  maxDropdownHeight?: string
}

export function PaginatedSelect<T>({
  value,
  onChange,
  placeholder,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onFetchNextPage,
  options,
  getOptionValue,
  getOptionLabel,
  disabled = false,
  className = '',
  loadingText = 'Loading...',
  dropdownDirection = 'auto',
  maxDropdownHeight = '16rem'
}: PaginatedSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [shouldDropUp, setShouldDropUp] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  // Refs to prevent multiple rapid calls
  const lastScrollTimeRef = useRef<number>(0)
  const fetchingRef = useRef<boolean>(false)

  // Memoize filtered options to prevent unnecessary recalculations
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options
    return options.filter(option =>
      getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [options, searchTerm, getOptionLabel])

  // Find selected option
  const selectedOption = useMemo(() => 
    options.find(option => getOptionValue(option) === value),
    [options, value, getOptionValue]
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const { target } = event
      if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => { document.removeEventListener('mousedown', handleClickOutside) }
    }
    
    return undefined
  }, [isOpen])

  // Track fetching state
  useEffect(() => {
    fetchingRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  // Debounced scroll handler to prevent multiple rapid calls
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const now = Date.now()
    
    // Prevent rapid scroll events (debounce 300ms)
    if (now - lastScrollTimeRef.current < 300) {
      return
    }
    
    // Don't load more if already loading or no more pages
    if (fetchingRef.current || !hasNextPage) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
    
    // Only trigger when scrolled 85% down to reduce false triggers
    if (scrollPercentage >= 0.85) {
      console.log('[PaginatedSelect] Scroll trigger - loading next page...')
      lastScrollTimeRef.current = now
      fetchingRef.current = true
      onFetchNextPage()
    }
  }, [hasNextPage, onFetchNextPage])

  // Handle option selection
  const handleOptionSelect = useCallback((option: T) => {
    onChange(getOptionValue(option))
    setIsOpen(false)
    setSearchTerm('')
  }, [onChange, getOptionValue])

  // Handle manual load more button
  const handleLoadMore = useCallback(() => {
    if (!fetchingRef.current && hasNextPage) {
      console.log('[PaginatedSelect] Manual load more clicked...')
      fetchingRef.current = true
      onFetchNextPage()
    }
  }, [hasNextPage, onFetchNextPage])

  // Handle key navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearchTerm('')
    } else if (e.key === 'Enter' && filteredOptions.length === 1) {
      handleOptionSelect(filteredOptions[0])
    }
  }, [filteredOptions, handleOptionSelect])

  // Focus input when dropdown opens and calculate dropdown direction
  useEffect(() => {
    if (isOpen) {
      // Focus input
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus()
        }, 50)
      }
      
      // Calculate dropdown direction
      if (dropdownDirection === 'auto' && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - buttonRect.bottom
        const spaceAbove = buttonRect.top
        
        // If not enough space below and more space above, drop up
        const dropdownHeight = 400 // Approximate max height
        setShouldDropUp(spaceBelow < dropdownHeight && spaceAbove > spaceBelow)
      } else {
        setShouldDropUp(dropdownDirection === 'up')
      }
    }
  }, [isOpen, dropdownDirection])

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Select Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => { if (!disabled) { setIsOpen(!isOpen) } }}
        disabled={disabled}
        className={`
          w-full px-2 py-1 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm
          ${disabled 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-600 dark:text-gray-500' 
            : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
          }
          border-gray-300 dark:border-gray-600
        `}
      >
        <div className="flex items-center justify-between">
          <span className="truncate">
            {selectedOption ? getOptionLabel(selectedOption) : placeholder}
          </span>
          <div className="flex items-center space-x-1">
            {isLoading && (
              <div className="w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
            )}
            {isOpen ? (
              <ChevronUpIcon className="w-3 h-3 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            )}
          </div>
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className={`absolute z-50 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg ${
            shouldDropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          style={{ maxWidth: '100%' }}>
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-600 dark:text-white"
            />
          </div>

          {/* Options List */}
          <div
            ref={listRef}
            className="overflow-y-auto overscroll-contain"
            style={{ maxHeight: maxDropdownHeight }}
            onScroll={handleScroll}
          >
            {isLoading && filteredOptions.length === 0 ? (
              <div className="flex items-center justify-center p-4 text-sm text-gray-500 dark:text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mr-2"></div>
                {loadingText}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                {searchTerm ? 'No matches found' : 'No options available'}
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => {
                  const optionValue = getOptionValue(option)
                  const optionLabel = getOptionLabel(option)
                  
                  return (
                    <button
                      key={`${optionValue}-${index}`}
                      type="button"
                      onClick={() => { handleOptionSelect(option) }}
                      className={`
                        w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600
                        ${optionValue === value 
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300' 
                          : 'text-gray-900 dark:text-white'
                        }
                      `}
                    >
                      <div className="truncate" title={optionLabel}>
                        {optionLabel}
                      </div>
                    </button>
                  )
                })}
                
                {/* Load More Indicator */}
                {(hasNextPage || isFetchingNextPage) && (
                  <div className="p-3 text-center border-t border-gray-200 dark:border-gray-600">
                    {isFetchingNextPage ? (
                      <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mr-2"></div>
                        Loading more...
                      </div>
                    ) : hasNextPage ? (
                      <button
                        type="button"
                        onClick={handleLoadMore}
                        disabled={isFetchingNextPage}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 focus:outline-none focus:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Load more options
                      </button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
